<script lang="ts" setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { LoadingListRoute } from '@/types/cargo-loading.js'
import { numberFormatter } from '@/utils/numbers'
import { formatRouteInfo } from '@/utils/cargo-loading'

import MyCheckbox from '@/components/my-components/form/MyCheckbox.vue'

interface Props {
    destination: string
    route: LoadingListRoute
    hideItems?: boolean
}

const props = withDefaults(defineProps<Props>(), { hideItems: false })
const { t } = useI18n()

const items = computed(() => {
    // The items should be sorted by last four digits in the awb number
    return [...props.route.items].sort((itemA, itemB) => {
        const last4A = parseInt(itemA.awbNumber.slice(-4), 10)
        const last4B = parseInt(itemB.awbNumber.slice(-4), 10)

        return last4A - last4B
    })
})

const itemTotals = computed(() => {
    return props.route.items.reduce(
        (totals, item) => {
            totals.totalAwbs += 1
            totals.totalAmount += item.amount
            totals.totalWeight += item.weight
            totals.totalVolume += item.volume

            return totals
        },
        { totalAwbs: 0, totalAmount: 0, totalWeight: 0, totalVolume: 0 },
    )
})
</script>

<template>
    <div
        class="relative border border-stone-300 dark:border-stone-600 rounded-lg p-4 bg-white dark:bg-dark-500 shadow-sm print:shadow-none print:break-inside-avoid print:rounded-none print:border-t print:border-b-[0] print:border-x-[0] print:px-0"
    >
        <div class="flex space-x-4 mb-1 text-sm font-bold">
            <slot name="routeInformation">
                <span v-text="formatRouteInfo(props.destination, props.route)" />

                <span v-text="props.route.remark" />
            </slot>
        </div>

        <template v-if="!props.hideItems">
            <div
                class="mt-4 grid grid-cols-6 gap-6 text-base text-stone-500 dark:text-primary-300 print:text-sm print:gap-2"
            >
                <div>{{ t('awb') }}</div>
                <div>{{ t('amount') }}</div>
                <div>{{ t('weight') }}</div>
                <div>{{ t('volume') }}</div>
                <div>{{ t('description') }}</div>
                <div>Spl</div>
            </div>

            <slot v-if="items && items.length > 0" name="items">
                <div
                    v-for="loadingListItem in items"
                    :key="loadingListItem.id"
                    class="grid grid-cols-6 gap-6 text-sm border-b py-2 print:gap-2"
                >
                    <span class="text-sm print:text-xs font-semibold">
                        {{ loadingListItem.awbNumber }}
                    </span>
                    <div
                        class="text-sm print:text-xs font-semibold print:flex print:justify-between print:items-start"
                    >
                        {{ loadingListItem.amount }}
                        <div class="hidden print:flex space-x-1 mr-2">
                            <MyCheckbox disabled />
                            <MyCheckbox disabled />
                        </div>
                    </div>
                    <span class="text-sm print:text-xs font-semibold">
                        {{ loadingListItem.weight }}
                        {{ t('kg') }}
                    </span>
                    <span class="text-sm print:text-xs font-semibold">
                        {{ loadingListItem.volume }}
                        {{ t('cbm') }}
                    </span>
                    <span class="text-sm print:text-xs font-semibold">
                        {{ loadingListItem.description }}
                    </span>
                    <span class="text-sm print:text-xs font-semibold">
                        {{ loadingListItem.spls?.join(', ') }}
                    </span>
                </div>
            </slot>

            <div
                v-else
                class="flex text-center text-sm py-4 text-stone-500 dark:text-stone-400 italic"
                v-text="t('noItemsAvailable')"
            />

            <div
                class="grid grid-cols-6 gap-6 text-sm py-2 text-primary-400 dark:text-primary-100 print:gap-2"
            >
                <span class="text-sm font-semibold print:text-xs">
                    {{ t('totalAwbs') }}
                    {{ itemTotals.totalAwbs }}
                </span>
                <span class="text-sm font-semibold print:text-xs">
                    {{ numberFormatter.format(itemTotals.totalAmount) }}
                </span>
                <span class="text-sm font-semibold print:text-xs">
                    {{ numberFormatter.format(itemTotals.totalWeight) }}
                    {{ t('kg') }}
                </span>
                <span class="text-sm font-semibold print:text-xs">
                    {{ numberFormatter.format(itemTotals.totalVolume) }}
                    {{ t('cbm') }}
                </span>

                <slot name="itemFooterActions" />
            </div>
        </template>
    </div>
</template>
